import React, { useState, useEffect } from "react";
import ReactQuill from "react-quill";
import axios from "axios";
import Skeleton from "react-loading-skeleton";
import "react-quill/dist/quill.snow.css";
import "react-loading-skeleton/dist/skeleton.css";
import { url } from "../constants/url";
import { useNavigate } from "react-router-dom";

const modules = {
  toolbar: [
    [{ header: "1" }, { header: "2" }, { font: [] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [
      { list: "ordered" },
      { list: "bullet" },
      { indent: "-1" },
      { indent: "+1" }
    ],
    ["link", "image", "video"],
    ["clean"]
  ]
};

const formats = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
  "video"
];

// Fallback default HTML if API has none
const defaultTerms = `
  <h1>Boothee’s Terms and Conditions</h1>
  <h2>Introduction:</h2>
  <p>These Terms and Conditions ("Agreement") govern the use of Boothee Salon Employment App ("Boothee"). By accessing or using Boothee, you agree to be bound by this Agreement.</p>
  <!-- etc... -->
`;

export default function TermsEditor() {
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const authHeader = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    axios
      .get(`${url}/api/admin/terms`, {
        headers: {
          "Content-Type": "application/json",
          ...authHeader()
        }
      })
      .then((res) => {
        setContent(res.data.terms?.content ?? defaultTerms);
      })
      .catch((err) => {
        console.error("Error fetching terms:", err);
        setContent(defaultTerms);
      })
      .finally(() => setLoading(false));
  }, []);

  const navigate = useNavigate();

  const handleSubmit = () => {
    setSubmitting(true);

    axios
      .post(
        `${url}/api/admin/terms`,
        { content },
        {
          headers: {
            "Content-Type": "application/json",
            ...authHeader()
          }
        }
      )
      .then(() => {
        setEditMode(false);
      })
      .catch((err) => {
        // Unauthorized → clear auth and redirect
        if (err.response?.status === 401) {
          localStorage.clear();
          navigate("/login");
          return;
        }

        console.error("Error saving terms:", err);
        alert("There was an error saving the Terms and Conditions.");
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <div className="p-6 bg-white rounded-3xl shadow-md">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-semibold text-[#AF9C80]">
          Terms and Conditions
        </h1>
        {!editMode && (
          <button
            onClick={() => setEditMode(true)}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg"
          >
            Edit
          </button>
        )}
      </div>
      <div className="bg-gray-100 rounded-lg shadow-inner p-4 mb-4">
        {loading ? (
          <Skeleton height={400} />
        ) : (
          <ReactQuill
            value={content}
            onChange={setContent}
            modules={modules}
            formats={formats}
            readOnly={!editMode}
            style={{ height: "400px" }}
          />
        )}
      </div>
      {editMode && (
        <button
          onClick={handleSubmit}
          disabled={loading || submitting}
          className="mt-4 p-3 bg-[#AF9C80] text-white font-semibold rounded-3xl shadow-md hover:bg-[#8e7a61]"
        >
          {submitting ? "Saving..." : "Save Terms"}
        </button>
      )}
    </div>
  );
}
